"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.servicePlanScheduleSchema = exports.servicePlanSchema = exports.serviceHistorySchema = exports.dailyActivitySchema = exports.technicianJobSchema = exports.dispatchJobSchema = exports.updateJobCardSchema = exports.jobCardSchema = exports.assetSchema = void 0;
exports.validateTimeRange = validateTimeRange;
exports.validateRecurringDuration = validateRecurringDuration;
const zod_1 = require("zod");
const croner_1 = require("croner");
const common_1 = require("./common");
const master_data_1 = require("./master-data");
const dateLike = zod_1.z.union([zod_1.z.string().datetime(), zod_1.z.string().min(1), zod_1.z.date()]).nullable().optional();
const numOrNull = zod_1.z.number().nullable().optional();
const strOrNull = zod_1.z.string().nullable().optional();
/**
 * Unified Asset — absorbed the legacy Vehicle + DispatchVehicle forms.
 * `company` (Company ref) + `fleetNumber` form a compound unique constraint,
 * so two assets can share a fleet number as long as they belong to different companies.
 */
exports.assetSchema = zod_1.z.object({
    fleetNumber: zod_1.z.string().min(1).trim(),
    company: common_1.objectIdSchema.nullable().optional(),
    registrationNumber: strOrNull,
    make: strOrNull,
    model: strOrNull,
    year: numOrNull,
    colour: strOrNull,
    vehicleType: common_1.objectIdSchema.nullable().optional(),
    vehicleGroup: common_1.objectIdSchema.nullable().optional(),
    department: common_1.objectIdSchema.nullable().optional(),
    branch: common_1.objectIdSchema.nullable().optional(),
    depot: common_1.objectIdSchema.nullable().optional(),
    serviceLocation: common_1.objectIdSchema.nullable().optional(),
    site: common_1.objectIdSchema.nullable().optional(),
    nztaType: strOrNull,
    subjectToRuc: zod_1.z.boolean().nullable().optional(),
    grossMassWeight: numOrNull,
    grossUnladenWeight: numOrNull,
    deviceSerialNumber: strOrNull,
    fuelMeasurementIn: zod_1.z.enum(['Liters', 'Gallons (US)', 'Gallons (UK)']).nullable().optional(),
    secondaryAuxiliaryMeter: zod_1.z.boolean().nullable().optional(),
    secondaryMeterUnit: zod_1.z.enum(['Kilometers', 'Miles', 'Hours']).nullable().optional(),
    dispatchGroup: common_1.objectIdSchema.nullable().optional(),
    // Denormalised; kept in sync with ServicePlan.assignedAssets via Asset
    // model hooks. Listed here so the picker on the asset form persists.
    servicePlan: common_1.objectIdSchema.nullable().optional(),
    status: strOrNull,
    vehicleStatus: strOrNull,
    serviceStatus: strOrNull,
    chassisNumber: strOrNull,
    engineNumber: strOrNull,
    serialNumber: strOrNull,
    fuelType: strOrNull,
    description: strOrNull,
    nztaRegistered: zod_1.z.boolean().nullable().optional(),
    deviceType: strOrNull,
    trackingProvider: strOrNull,
    gpsId: strOrNull,
    coretexId: strOrNull,
    blackhawkId: strOrNull,
    eroadId: strOrNull,
    iot: zod_1.z.boolean().optional(),
    currentOdo: numOrNull,
    currentHours: numOrNull,
    currentHubo: numOrNull,
    trackUsageAs: zod_1.z.enum(['Odometer', 'Hubometer', 'Hours']).nullable().optional(),
    mileage: numOrNull,
    regoExpiry: dateLike,
    wofExpiry: dateLike,
    cofExpiry: dateLike,
    yearOfManufacture: numOrNull,
    photo: strOrNull,
    photoKey: strOrNull,
    address: common_1.addressSchema,
    lat: numOrNull,
    lon: numOrNull,
    currentAddress: strOrNull,
    notes: strOrNull,
    showInDispatch: zod_1.z.boolean().optional(),
    online: zod_1.z.boolean().optional(),
    assetType: zod_1.z.enum(['Atlas', 'Contractor']).optional(),
});
const jobCardBaseSchema = zod_1.z.object({
    // Optional on create — the JobCard model's pre('validate') hook
    // auto-generates a unique sequential number when the caller omits it.
    // When supplied, must be a non-empty string.
    jobSheetNumber: zod_1.z.string().min(1).trim().optional(),
    asset: common_1.objectIdSchema.nullable().optional(),
    servicePlan: common_1.objectIdSchema.nullable().optional(),
    servicePlanSchedule: common_1.objectIdSchema.nullable().optional(),
    assignedTo: zod_1.z.array(common_1.objectIdSchema).optional(),
    // Workflow status (enum). Merged from the legacy `workflowStatus` field.
    status: zod_1.z.enum(['active', 'completed', 'approved', 'voided']).optional(),
    workDescription: strOrNull,
    // Scheduled job date — dispatchers can create cards in advance and set
    // when the work is meant to happen. Distinct from startDate/endDate
    // which are stamped when the technician actually starts/finishes work.
    jobDate: dateLike,
    startDate: dateLike,
    endDate: dateLike,
    completedTime: dateLike,
    totalTimeLogged: numOrNull,
    // Zoho-parity static classifications. Multi-select so a single job
    // can be tagged with several (Scheduled Service + WoF on the same
    // visit etc.). Server enforces the allowed values; client form is
    // a checkbox list with the same enum.
    jobType: zod_1.z.array(zod_1.z.enum(['Scheduled Service', 'Unscheduled Maintenance', 'WoF or CoF'])).optional(),
    // 'Atlas' (serviced in-house at a service location) | 'Vendor' (serviced
    // by an external supplier). Drives which lookup field the form renders
    // next to it. Stored as a string for backwards-compat with legacy rows
    // that may have free-text values.
    servicedWith: strOrNull,
    // Where / by whom the asset was serviced. Mutually exclusive in the UI
    // (one renders depending on `servicedWith`), but the schema accepts
    // either — legacy rows may have both set, and an admin editing an old
    // record shouldn't be forced to wipe one to save the other.
    serviceLocation: common_1.objectIdSchema.nullable().optional(),
    vendor: common_1.objectIdSchema.nullable().optional(),
    reviewNotes: strOrNull,
});
/**
 * A job card is the only place the operator declares what kind of visit this
 * is, and `servicePlanSchedule` propagates from here onto the ServiceHistory
 * that the post-save hook opens (job-card.model.ts). When it is null there,
 * the next-due compute cannot see the completed service at all — the schedule
 * reports overdue forever no matter how much work is done.
 *
 * On prod this is the dominant leak: `jobType` was empty on 5,472 of 6,281
 * job cards, and those empty-jobType cards produced 681 unlinked 2026 service
 * records. Cards that DID declare "Scheduled Service" were linked 518 of 538.
 * Operators pick a schedule when the form asks and skip it entirely when it
 * does not, so the gate has to sit on `jobType` itself — a rule conditional on
 * jobType alone would have caught only 20 records.
 *
 * CREATE enforces both rules. UPDATE deliberately does NOT require `jobType`:
 * the bulk-edit path parses a sparse patch through this same schema, and all
 * 5,472 legacy cards would fail on any edit. Update only rejects a patch that
 * actively selects a Scheduled Service type while nulling the schedule.
 */
const SCHEDULED_JOB_TYPE = 'Scheduled Service';
exports.jobCardSchema = jobCardBaseSchema.superRefine((v, ctx) => {
    // The asset is what makes the whole record mean anything, and leaving it
    // optional on create has a specific, measured cost: the JobCard post-save
    // hook opens a ServiceHistory ~150ms after insert stamped with
    // `asset: doc.asset ?? null`. A card created without an asset therefore
    // opens an ORPHANED service record, and setting the asset afterwards updates
    // only the card — nothing back-fills the record. 126 such records exist on
    // prod; fleet 104's job sheet 38085 is one, which is why an A-Service done
    // and approved on 2026-08-24 still read as 65 days overdue.
    if (!v.asset) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom, path: ['asset'],
            message: 'Select the asset this job card is for.',
        });
    }
    if (!v.jobType || v.jobType.length === 0) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom, path: ['jobType'],
            message: 'Select at least one Job Type.',
        });
        return;
    }
    if (v.jobType.includes(SCHEDULED_JOB_TYPE) && !v.servicePlanSchedule) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom, path: ['servicePlanSchedule'],
            message: 'Service Schedule is required for a Scheduled Service job card.',
        });
    }
});
// Update schema allows partial payloads — the form may omit unchanged fields,
// and bulk edit sends only the changed keys. See the note above for why the
// jobType requirement is create-only.
exports.updateJobCardSchema = jobCardBaseSchema.partial().superRefine((v, ctx) => {
    if (v.jobType?.includes(SCHEDULED_JOB_TYPE) && 'servicePlanSchedule' in v && !v.servicePlanSchedule) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom, path: ['servicePlanSchedule'],
            message: 'Service Schedule is required for a Scheduled Service job card.',
        });
    }
});
const recurrencePatternSchema = zod_1.z.object({
    cron: zod_1.z.string().min(1),
    timezone: zod_1.z.string().min(1),
}).nullable().optional();
/**
 * Detect the "recurring multi-day job" foot-gun: if the job's duration
 * (end − start) is ≥ the minimum gap between cron fires, every spawn
 * would overlap with the previous one — confusing on the board AND
 * lossy in the spawner (which currently collapses multi-day templates
 * to a single time-of-day on each fire).
 *
 * Returns a human-readable error message, or `null` when the combo is
 * sane (or when the inputs don't give us enough info to judge — e.g.
 * no end date, no cron, inverted dates).
 *
 * Implementation: croner doesn't expose a "minimum gap" API, so we
 * sample the next ~10 fires and take min(diffs). That's robust to
 * uneven schedules like `0 6 * * 1-5` (24h weekday gaps, 72h Friday
 * to Monday — the 24h is the binding constraint).
 */
/**
 * End must be strictly after Start. Returns a human-readable error, or null
 * when the range is valid or incomplete (missing/unparseable dates are left
 * to required-field validation). Canonical range check — used by the dispatch
 * job dialogs (create / edit / duplicate) and the dispatchJobSchema refine.
 */
function validateTimeRange(start, end) {
    if (!start || !end)
        return null;
    const s = start instanceof Date ? start.getTime() : new Date(start).getTime();
    const e = end instanceof Date ? end.getTime() : new Date(end).getTime();
    if (Number.isNaN(s) || Number.isNaN(e))
        return null;
    return e > s ? null : 'End must be after Start.';
}
function validateRecurringDuration(start, end, cron, timezone) {
    if (!start || !end || !cron)
        return null;
    const s = start instanceof Date ? start : new Date(start);
    const e = end instanceof Date ? end : new Date(end);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()))
        return null;
    const durationMs = e.getTime() - s.getTime();
    if (durationMs <= 0)
        return null; // other validation handles inverted
    let job;
    try {
        job = new croner_1.Cron(cron, { timezone: timezone ?? 'Pacific/Auckland', paused: true });
    }
    catch {
        return null; // invalid cron — separate validation surfaces that
    }
    const fires = [];
    let prev = null;
    for (let i = 0; i < 10; i++) {
        const next = job.nextRun(prev ?? new Date());
        if (!next)
            break;
        fires.push(next.getTime());
        prev = next;
    }
    if (fires.length < 2)
        return null; // can't compute a gap from a single fire
    let minGapMs = Infinity;
    for (let i = 1; i < fires.length; i++) {
        const gap = fires[i] - fires[i - 1];
        if (gap > 0 && gap < minGapMs)
            minGapMs = gap;
    }
    if (!Number.isFinite(minGapMs))
        return null;
    if (durationMs < minGapMs)
        return null;
    const fmt = (ms) => {
        const totalMinutes = Math.round(ms / 60000);
        const days = Math.floor(totalMinutes / (24 * 60));
        const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
        const mins = totalMinutes % 60;
        const parts = [];
        if (days)
            parts.push(`${days}d`);
        if (hours)
            parts.push(`${hours}h`);
        if (mins)
            parts.push(`${mins}m`);
        return parts.length ? parts.join(' ') : '0m';
    };
    return `Job duration (${fmt(durationMs)}) is ≥ the recurrence interval (${fmt(minGapMs)}). Each recurrence would overlap the previous job — shorten the end date or pick a longer recurrence interval.`;
}
exports.dispatchJobSchema = zod_1.z.object({
    jobNumber: strOrNull,
    hiddenDispatchNumber: strOrNull,
    customer: strOrNull,
    // Link to the Customer record; `customer` keeps the name text alongside it.
    customerRef: common_1.objectIdSchema.nullable().optional(),
    // Inline new customer (the form's "Other" option). Never stored on the job:
    // the server creates the Customer in the same transaction and links it.
    // Site / Weightrax flag are set by the server from the job.
    newCustomer: master_data_1.customerBaseSchema.optional(),
    // Loading site / weighbridge; its Weightrax credentials are used for the
    // job. Required unless the job is excluded from Weightrax (route-enforced).
    site: common_1.objectIdSchema.nullable().optional(),
    // Never send this job to Weightrax; makes Site optional.
    excludeFromWeightrax: zod_1.z.boolean().optional(),
    fleetNumber: strOrNull,
    asset: common_1.objectIdSchema, // required: every dispatch is FOR an asset
    jobDetails: strOrNull,
    info: strOrNull,
    start: dateLike,
    end: dateLike,
    phoneNumber: strOrNull,
    status: strOrNull,
    address: common_1.addressSchema,
    driver: common_1.objectIdSchema.nullable().optional(),
    completionNotes: strOrNull.optional(),
    // Recurrence — "baton-pass" model. Only the current chain head has
    // `recurring: true`; spawned occurrences inherit it and the source
    // flips to false. See server/src/services/dispatch-job-recurrence.ts
    // for the spawn logic.
    recurring: zod_1.z.boolean().optional(),
    recurrencePattern: recurrencePatternSchema,
    recurringFromId: common_1.objectIdSchema.nullable().optional(),
    recurringToId: common_1.objectIdSchema.nullable().optional(),
}).superRefine((v, ctx) => {
    // Only judge the range when the payload carries both ends (a status-only
    // PUT like Mark Complete must not be rejected over fields it didn't send).
    const rangeError = validateTimeRange(v.start, v.end);
    if (rangeError)
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: rangeError, path: ['end'] });
}).refine((v) => !v.recurring || !!v.recurrencePattern?.cron, { message: 'Recurring dispatch jobs require a non-empty recurrencePattern.cron', path: ['recurrencePattern'] }).superRefine((v, ctx) => {
    if (!v.recurring)
        return;
    const msg = validateRecurringDuration(v.start, v.end, v.recurrencePattern?.cron, v.recurrencePattern?.timezone);
    if (msg)
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: msg, path: ['recurrencePattern', 'cron'] });
});
exports.technicianJobSchema = zod_1.z.object({
    jobNumber: strOrNull,
    hiddenNumber: strOrNull,
    technicianName: strOrNull,
    technician: common_1.objectIdSchema, // required: a technician job needs a technician
    customer: strOrNull,
    start: dateLike,
    end: dateLike,
    jobDetails: strOrNull,
    info: strOrNull,
    status: strOrNull,
});
exports.dailyActivitySchema = zod_1.z.object({
    asset: common_1.objectIdSchema, // required: daily activity is for one asset
    fleetNumber: strOrNull,
    date: dateLike,
    currentOdometer: numOrNull,
    currentEngineHours: numOrNull,
    distanceDriven: numOrNull,
    engineHoursUtilised: numOrNull,
    processed: zod_1.z.boolean().optional(),
});
exports.serviceHistorySchema = zod_1.z.object({
    jobSheetNumber: strOrNull,
    jobDate: dateLike,
    asset: common_1.objectIdSchema, // required: service history records work on one asset
    // Ref fields the Log Service form persists. Zod's default `.strip()` was
    // silently dropping these before they were listed here — the user could
    // pick a Service Plan / Vendor / Serviced By etc. but nothing saved.
    servicePlan: common_1.objectIdSchema.nullable().optional(),
    servicePlanSchedule: common_1.objectIdSchema.nullable().optional(),
    vendor: common_1.objectIdSchema.nullable().optional(),
    servicedBy: common_1.objectIdSchema.nullable().optional(),
    jobCode: common_1.objectIdSchema.nullable().optional(),
    serviceLocation: common_1.objectIdSchema.nullable().optional(),
    fleetNumber: strOrNull,
    descriptionOfWork: strOrNull,
    faultDescriptions: strOrNull,
    hubo: numOrNull,
    odometer: numOrNull,
    engineHours: numOrNull,
    invoice: strOrNull,
    invoiceNumber: strOrNull,
    labor: numOrNull,
    parts: numOrNull,
    tax: numOrNull,
    total: numOrNull,
    wofCof: strOrNull,
    expiryDate: dateLike,
    status: strOrNull,
    nextOilChangeMileage: numOrNull,
    servicedWith: strOrNull,
    /** Picklist driving conditional sections in the Log Service form. See the
     *  ServiceHistory model for the value semantics. */
    serviceType: strOrNull,
});
exports.servicePlanSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).trim(),
    assignedAssets: zod_1.z.array(common_1.objectIdSchema).optional(),
});
exports.servicePlanScheduleSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).trim(),
    // The parent plan. Listed here so Zod's default .strip() doesn't
    // silently drop the form's Service Plan picker on save.
    servicePlan: common_1.objectIdSchema.nullable().optional(),
    recurring: zod_1.z.boolean().optional(),
    serviceInterval: numOrNull,
    unitOfMeasurement: strOrNull,
    formula: strOrNull,
    editChecklist: zod_1.z.boolean().optional(),
    // Marks the schedule as the engine-oil row for the Service Report's
    // "Engine Oil Due" column. The model's pre-save hook flips
    // `isOilChangeAuto` to false when this field is set explicitly so
    // future name edits won't trample the operator's decision.
    isOilChange: zod_1.z.boolean().optional(),
    // Other schedules in the same plan that completing this one also
    // fulfils — see the model for full semantics. The model's pre-save
    // validator enforces same-plan and self-include rules, so the Zod
    // schema only needs to accept the shape.
    includes: zod_1.z.array(common_1.objectIdSchema).optional(),
});
//# sourceMappingURL=fleet-ops.js.map